import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { ToastOptionsSuccess, ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiHosts } from "@/api/constants/apiHosts";
import type { AxiosResponse } from "axios";
import { LE } from "@/constants/enums";
import type { EnforcementAction, EnforcementActionNew, EnforcementActionQuery } from "./enforcementAction";
import enforcementActionService from "./enforcementActionService";

const URL = "enforcement-actions";
const ENTITY_NAME = "Enforcement Action";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<EnforcementAction>(apiHosts.AQMS, axiosParams);

const getEnforcementActions = (params: EnforcementActionQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

type EnforcementActionIds = {
  id?: string | number;
};
const getNewEnforcementAction = async (ids: EnforcementActionIds) => {
  const defaultApp = {
    data: {
      ...enforcementActionService.getInitialData(),
      ...ids,
      typeMurn: LE.EnforcementActionType.Non,
    },
  } as AxiosResponse<EnforcementActionNew, any>;

  return defaultApp;
};

const getEnforcementActionById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const createEnforcementAction = (enforcementAction: EnforcementAction, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.post(URL, enforcementAction, {
    successToastCb,
    errorToastCb,
  });
};

const updateEnforcementAction = (enforcementAction: EnforcementAction, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.put(URL + "/" + enforcementAction.id, enforcementAction, {
    successToastCb,
    errorToastCb,
  });
};

// const getEnforcementActionByIdOrNew = (ids: EnforcementAction, config: PagingApiAndNotifierConfig = {}) => {
//   if (!ids.id) {
//     return getNewEnforcementAction(ids);
//   } else {
//     return getEnforcementActionById(ids.id, config) as Promise<AxiosResponse<EnforcementActionNew, any>>;
//   }
// };

/**
 * @param {EnforcementAction} enforcementAction This cannot be a 'ref' to a enforcementAction
 */
const saveEnforcementAction = (enforcementAction: EnforcementAction, config = {}) => {
  if (enforcementAction.id) {
    return updateEnforcementAction(enforcementAction, config);
  } else {
    return createEnforcementAction(enforcementAction, config);
  }
};

const deleteEnforcementActionById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

export {
  deleteEnforcementActionById,
  getEnforcementActions,
  getEnforcementActionById,
  getNewEnforcementAction,
  // getEnforcementActionByIdOrNew,
  createEnforcementAction,
  updateEnforcementAction,
  saveEnforcementAction,
};
